/// <amd-module name="Test/TestPlansLibrary/TestCasesPivotFilter/Redux/TestCasesPivotFilterModule" />
import { ISagaModule } from "redux-dynamic-modules-saga";
import { ActionsUnion } from "redux/Utils/ActionHelper";

import { LibraryQueryConstants } from "Test/TestPlansLibrary/LibraryHub/Constants";
import { TestPlansLibraryReducer } from "Test/TestPlansLibrary/LibraryHub/Redux/TestPlansLibraryReducer";

import { TestCasesReducer } from "Test/TestPlansLibrary/Pivots/TestCasesPivot/Redux/TestCasesReducer";
import { TestCasesSaga } from "Test/TestPlansLibrary/Pivots/TestCasesPivot/Redux/TestCasesSaga";

import {
    ITestPlansLibraryFilterBarItemBase,
    ITestPlansLibraryFilterBarItem,
    ITestPlansLibraryFilterBarItemIdentity,
    ITestPlansLibraryFilterItem,
    TestPlansLibraryFilterItemType
} from "Test/TestPlansLibrary/TestPlansLibraryFilter/TestPlansLibraryFilterContracts";

import { TastCasesPivotFilterActions } from "./TestCasesPivotFilterActions";
import { ITestCasesPivotFilterAwareState, ITestCasesPivotFilterBarState } from "./TestCasesPivotFilterContracts";
import { TestCasesPivotFilterReducer } from "./TestCasesPivotFilterReducer";
import { TestCasesPivotFilterSaga } from "./TestCasesPivotFilterSaga";
import * as Constants from "../TestCasesPivotFilterConstants";
import * as Resources from "Test/TestPlansLibrary/TestCasesPivotFilter/Resources";

/**
 * Initializes the test cases pivot filter module
 */
export function getTestCasesPivotFilterModule(): ISagaModule<ITestCasesPivotFilterAwareState> {
    const initialActions: ActionsUnion<typeof TastCasesPivotFilterActions>[] = [];
    initialActions.push(TastCasesPivotFilterActions.initializeFilterBarState(_getInitialFilterBarState()));

    return {
        id: "test-cases-filters",
        reducerMap: {
            filterBarState: TestCasesPivotFilterReducer,
            testCasesState: TestCasesReducer,
            testPlansLibraryState: TestPlansLibraryReducer
        },
        sagas: [TestCasesPivotFilterSaga, TestCasesSaga],
        initialActions: initialActions
    };
}

/**
 * Functions that help build the initial filter bar state
 * **This shouldn't be consumed from outside of this file, exporting it for L0 tests**
 */
export function _getInitialFilterBarState(): ITestCasesPivotFilterBarState {
    return {
        filterBarItems: _getFilterBarItems(),
        assignedToSelection: []
    };
}

function _getFilterBarItems(): ITestPlansLibraryFilterBarItemBase[] {
    let filterBarItems: ITestPlansLibraryFilterBarItemBase[];

    const queriesDropdownFilterItem: ITestPlansLibraryFilterBarItem = {
        filterItemType: TestPlansLibraryFilterItemType.QueryDropdown,
        key: "testcases-queries-filter",
        filterItemKey: Constants.TestCasesQueriesFilterItemKey,
        // for now All test cases query is shown as default query
        placeholder: Resources.QueryTextAllTestCases,
        items: _getTestCaseQueryFilterItems(),
        width: 350
    };

    const textFilterItem: ITestPlansLibraryFilterBarItemBase = {
        filterItemType: TestPlansLibraryFilterItemType.TextFilter,
        key: "testcases-filter-text",
        filterItemKey: Constants.TestCasesTextFilterItemKey,
        placeholder: Resources.TextFilterPlaceholderText
    };

    const stateDropdownFilterItem: ITestPlansLibraryFilterBarItemBase = {
        filterItemType: TestPlansLibraryFilterItemType.DropDownFilter,
        key: "testcases-state-filter",
        filterItemKey: Constants.TestCasesStateFilterItemKey,
        placeholder: Resources.StateFilterPlaceholderText,
        width: 160
    };

    const assignedToDropdownFilterItem: ITestPlansLibraryFilterBarItemIdentity = {
        filterItemType: TestPlansLibraryFilterItemType.IdentityPicker,
        key: "testcases-assignedto-filter",
        filterItemKey: Constants.TestCasesAssignedToFilterItemKey,
        placeholder: Resources.AssignedToFilterPlaceholderText,
        width: 220
    };

    const priorityDropdownFilterItem: ITestPlansLibraryFilterBarItemBase = {
        filterItemType: TestPlansLibraryFilterItemType.DropDownFilter,
        key: "testcases-priority-filter",
        filterItemKey: Constants.TestCasesPriorityFilterItemKey,
        placeholder: Resources.PriorityFilterPlaceholderText,
        width: 160
    };

    const areaDropdownFilterItem: ITestPlansLibraryFilterBarItemBase = {
        filterItemType: TestPlansLibraryFilterItemType.DropDownFilter,
        key: "testcases-areapath-filter",
        filterItemKey: Constants.TestCasesAreaFilterItemKey,
        placeholder: Resources.AreaFilterPlaceholderText,
        width: 160
    };

    const tagsDropdownFilterItem: ITestPlansLibraryFilterBarItemBase = {
        filterItemType: TestPlansLibraryFilterItemType.DropDownFilter,
        key: "testcases-tags-filter",
        filterItemKey: Constants.TestCasesTagsFilterItemKey,
        placeholder: Resources.TagsFilterPlaceholderText,
        width: 160
    };

    filterBarItems = [
        queriesDropdownFilterItem,
        textFilterItem,
        assignedToDropdownFilterItem,
        stateDropdownFilterItem,
        priorityDropdownFilterItem,
        areaDropdownFilterItem,
        tagsDropdownFilterItem
    ];

    return filterBarItems;
}

function _getTestCaseQueryFilterItems(): ITestPlansLibraryFilterItem[] {
    return [
        {
            id: LibraryQueryConstants.QueryAllTestCasesKey,
            text: Resources.QueryTextAllTestCases
        },
        {
            id: LibraryQueryConstants.QueryTestsWithActiveBugsKey,
            text: Resources.QueryTextTestCasesWithActiveBugs
        },
        {
            id: LibraryQueryConstants.QueryTestsWithoutRequirementsKey,
            text: Resources.QueryTextTestCasesWithoutRequirements
        },
        {
            id: LibraryQueryConstants.QueryTestsWithRequirementsKey,
            text: Resources.QueryTextTestCasesWithRequirements
        }
    ];
}
